import { SortingRule } from 'react-table';
import { atom, selector } from 'recoil';

import { GetReportRequest } from 'api';
import { upperCaseFirstLetter, getFinancialYear } from 'tools';
import { PaginationInfo, Report, ReportData, ReportFilter } from 'types';

export const openReportModalState = atom<boolean>({
  key: 'openReportModal',
  default: false,
});

export const openReportModalMobileState = atom<boolean>({
  key: 'openReportModalMobile',
  default: false,
});

export const reportState = atom<Report | undefined>({
  key: 'report',
  default: undefined,
});

export const reportStartDateState = atom<Date | undefined>({
  key: 'reportStartDate',
  default: undefined,
});

export const reportEndDateState = atom<Date>({
  key: 'reportEndDate',
  default: new Date(),
});

export const reportFiltersState = atom<ReportFilter[]>({
  key: 'reportFilters',
  default: [],
});

export const reportTextState = atom<string>({
  key: 'reportTextState',
  default: '',
});

export const annualDfiReportYearState = atom<number>({
  key: 'annualDfiReportYear',
  default: getFinancialYear(),
});

export const tqEnAnnualReportYearState = atom<number>({
  key: 'tqEnAnnualReportYear',
  default: getFinancialYear(),
});

export const reportOrderByColumnState = atom<string | undefined>({
  key: 'reportOrderByColumn',
  default: undefined,
});

export const reportOrderByDescendingState = atom<boolean>({
  key: 'reportOrderByDescending',
  default: false,
});

export const reportPageSizeState = atom<number>({
  key: 'reportPageSize',
  default: 10,
});

export const reportPageNumberState = atom<number>({
  key: 'reportPageNumber',
  default: 1,
});

export const reportDataState = atom<ReportData[] | undefined>({
  key: 'reportData',
  default: undefined,
});

export const reportPaginationState = atom<PaginationInfo | undefined>({
  key: 'reportPagination',
  default: undefined,
});

export const reportSortByState = selector<SortingRule<ReportData>[]>({
  key: 'reportSortBy',
  get: ({ get }) => {
    const orderByColumn = get(reportOrderByColumnState);
    const orderByDescending = get(reportOrderByDescendingState);
    return orderByColumn ? [{ id: orderByColumn, desc: orderByDescending }] : [];
  },
});

export const reportRequestState = selector<GetReportRequest>({
  key: 'reportRequest',
  get: ({ get }) => {
    const report = get(reportState);
    const startDate = get(reportStartDateState);
    const endDate = get(reportEndDateState);
    const orderByColumn = get(reportOrderByColumnState);

    const request: GetReportRequest = {
      ...(report?.hasDateFilter && startDate ? { startDate: startDate.toISOString() } : null),
      ...(!report || report?.hasDateFilter ? { endDate: endDate.toISOString() } : null),
      pageSize: get(reportPageSizeState),
      pageNumber: get(reportPageNumberState),
      ...(orderByColumn
        ? {
            orderByColumn: upperCaseFirstLetter(orderByColumn),
            orderByDescending: get(reportOrderByDescendingState),
          }
        : null),
    };

    get(reportFiltersState).forEach((filter) => {
      request[filter.name] = filter.selectedItems.map((selected) => selected.value);
    });

    return request;
  },
});
